import type {
  HeartChronicleOutcome,
  HeartChroniclePhase,
  HeartChroniclePublicProjection,
} from "./heart-chronicle-public-projection";

/** The only projection fields the public campaign UI is allowed to consume. */
export const HEART_CHRONICLE_PUBLIC_PROJECTION_KEYS = [
  "schema",
  "chroniclePublicId",
  "phase",
  "heldFrontCount",
  "fronts",
  "convergenceDeadline",
  "lastOutcome",
  "revision",
] as const;

export type HeartChronicleLoadStatus =
  | "loading"
  | "fresh"
  | "refreshing"
  | "stale"
  | "error";

export interface HeartChronicleReadModel {
  readonly status: HeartChronicleLoadStatus;
  readonly projection?: HeartChroniclePublicProjection;
  readonly readAtMs: number | null;
  readonly error?: "Chronique indisponible";
  readonly announcement?: "Nouvelle Chronique chargée";
}

export interface HeartChroniclePresentation {
  readonly phaseText: string;
  readonly heldFrontsText: string;
  readonly crownStateText: string;
  readonly outcomeText: string;
  readonly deadlineText: string;
  readonly freshnessText: string;
  readonly orientationText: string;
}

const PHASE_TEXT: Record<HeartChroniclePhase, (heldFrontCount: number) => string> = {
  sealed: (heldFrontCount) => `Couronne scellée — ${heldFrontCount}/6 Fronts tenus.`,
  converging: (heldFrontCount) => `Les Fronts convergent — ${heldFrontCount}/6 tenus.`,
  exposed: (heldFrontCount) => `Couronne exposée — ${heldFrontCount}/6 Fronts tenus.`,
  sieging: (heldFrontCount) => `Siège du Cœur en cours — ${heldFrontCount}/6 Fronts tenus.`,
  stabilizing: (heldFrontCount) => `Le Cœur se stabilise — ${heldFrontCount}/6 Fronts tenus.`,
  respite: () => "Répit en cours.",
  resetting: () => "Chronique en réinitialisation — lecture seule.",
};

const CROWN_STATE_TEXT: Record<HeartChroniclePhase, string> = {
  sealed: "scellée",
  converging: "en convergence",
  exposed: "exposée",
  sieging: "assiégée",
  stabilizing: "en stabilisation",
  respite: "en répit",
  resetting: "en réinitialisation",
};

const OUTCOME_TEXT: Record<HeartChronicleOutcome, string> = {
  none: "Aucun résultat public.",
  repelled: "Dernier résultat : repoussé.",
  stabilized: "Dernier résultat : stabilisé.",
};

export const HEART_CHRONICLE_READ_INTERVAL_MS = 60_000;

export function createHeartChronicleReadModel(): HeartChronicleReadModel {
  return {
    status: "loading",
    readAtMs: null,
  };
}

export function beginHeartChronicleRead(model: HeartChronicleReadModel): HeartChronicleReadModel {
  return {
    ...model,
    status: model.projection === undefined ? "loading" : "refreshing",
    error: undefined,
  };
}

export function settleHeartChronicleSuccess(
  model: HeartChronicleReadModel,
  projection: HeartChroniclePublicProjection,
  readAtMs: number,
): HeartChronicleReadModel {
  const replaced = model.projection !== undefined &&
    model.projection.chroniclePublicId !== projection.chroniclePublicId;
  return {
    status: "fresh",
    projection,
    readAtMs,
    error: undefined,
    announcement: replaced ? "Nouvelle Chronique chargée" : undefined,
  };
}

export function settleHeartChronicleFailure(model: HeartChronicleReadModel): HeartChronicleReadModel {
  return {
    ...model,
    status: model.projection === undefined ? "error" : "stale",
    error: "Chronique indisponible",
  };
}

export function formatHeartChronicleDeadline(
  deadlineMs: number | null,
  readAtMs: number | null,
  nowMs: number,
): string {
  const label = "Échéance publique de convergence";
  if (deadlineMs === null) return "Aucune échéance publique de convergence.";

  const absoluteLocalTime = new Date(deadlineMs).toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
  if (deadlineMs <= nowMs) return `${label} : ${absoluteLocalTime} · estimation périmée`;
  if (readAtMs === null) return `${label} : ${absoluteLocalTime} · estimation indisponible`;

  const minutesAtLastRead = Math.max(0, Math.round((deadlineMs - readAtMs) / 60_000));
  return `${label} : ${absoluteLocalTime} · environ ${minutesAtLastRead} min à la dernière lecture`;
}

export function formatHeartChronicleFreshness(
  status: "fresh" | "stale",
  readAtMs: number | null,
): string {
  if (readAtMs === null) return "Lecture indisponible";
  const absoluteLocalTime = new Date(readAtMs).toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
  return status === "stale"
    ? `Périmée · dernière lecture ${absoluteLocalTime}`
    : `Fraîche · dernière lecture ${absoluteLocalTime}`;
}

export function presentHeartChronicle(
  projection: HeartChroniclePublicProjection,
  readAtMs: number | null,
  nowMs: number,
  status: "fresh" | "stale" = "fresh",
): HeartChroniclePresentation {
  return {
    phaseText: PHASE_TEXT[projection.phase](projection.heldFrontCount),
    heldFrontsText: `${projection.heldFrontCount}/6 Fronts tenus`,
    crownStateText: `État public de la Couronne : ${CROWN_STATE_TEXT[projection.phase]}.`,
    outcomeText: OUTCOME_TEXT[projection.lastOutcome],
    deadlineText: formatHeartChronicleDeadline(projection.convergenceDeadline, readAtMs, nowMs),
    freshnessText: formatHeartChronicleFreshness(status, readAtMs),
    orientationText: "Lecture publique verrouillée : aucune commande, socket ou ticket n’est créé.",
  };
}
